import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { v1 as uuidv1 } from "uuid";
import { useNavigate } from "react-router-dom";
import { ADD_CULTURES, ADD_CURRENCIES, AppContext } from "../../context";
import Header from "../../Components/Common/Header";
import ContentLayout from "../../Components/Styles/ContentLayout";
import SubscriptionInfo from "./SubscriptionInfo";
import SubscriptionTerms from "./SubscriptionTerms";
import Busy from "../../Components/Common/busy";
import SubscriptionModal from "./SubscriptionModal";
import AppLayout from "../../Components/Styles/AppLayout";
import ErrorPage from "../ErrorPage";
import UpdateSubscription from "./UpdateSubscription";
import SubscriptionCancelled from "./SubscriptionCancelled";
import { isBefore } from "date-fns";

const query = gql`
  query SubscriptionQuery(
    $saleId: Guid
    $posId: Int
    $culture: String
    $subscriptionId: Guid
  ) {
    paymentMethods(culture: $culture, posId: $posId, saleId: $saleId) {
      netseasy {
        id
        localName
        isActive
        settings {
          key
          value
        }
      }
    }
    subscriptionById(subscriptionId: $subscriptionId) {
      id
      subscriptionArticle {
        name
        plu
        priceInclVat
        priceExclVat
      }
      subscriptionTypeName
      civicRegistrationNumber
      isCancelled
      isAborted
      firstname
      lastname
      email
      customerId
      subscriptionAlreadyRegistered
      finalizingRegistrationChargeSaleId
      termType
      fixedTermNumberOfPeriods
      mustFinalizeBefore
    }
    cultures {
      name
      description
    }
  }
`;

const Subscription = () => {
  const { language, config } = useSelector((state) => ({
    config: state.config,
    language: state.language,
  }));

  const context = useContext(AppContext);
  const ctxDispatch = context.dispatch;
  const navigate = useNavigate();

  // Get subscriptionId, culture and posId from url
  const url = new URL(window.location);
  const params = new URLSearchParams(url.search);
  const subscriptionId = params.get("subscriptionId");
  const culture = params.get("cul");
  const posId = Number(params.get("pos"));

  const updateSubscription =
    window.location.pathname.includes("updateSubscription");

  const [newSaleId] = useState(uuidv1());

  const saleId = localStorage.getItem("saleId") || newSaleId;

  localStorage.setItem("saleId", saleId);

  const [goToPayment, setGoToPayment] = useState(false);

  const { data, loading, error } = useQuery(query, {
    variables: {
      subscriptionId,
      saleId,
      posId,
      culture,
    },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (config && language && typeof window !== "undefined") {
      if (window.location.pathname.includes("updateSubscription")) {
        document.title = `${language.Subscription_Update_Header} - ${config.shopName}`;
      } else {
        document.title = `${language.Subscription_Header} - ${config.shopName}`;
      }
    }
  }, [config, language, window]);

  useEffect(() => {
    if (data) {
      ctxDispatch({ type: ADD_CULTURES, payload: data.cultures });
      ctxDispatch({ type: ADD_CURRENCIES, payload: data.currencies });
    }
  }, [data]);

  const onboardingHasExpired = useMemo(() => {
    if (data?.subscriptionById?.subscriptionAlreadyRegistered) {
      return false;
    }
    if (
      isBefore(new Date(data?.subscriptionById?.mustFinalizeBefore), new Date())
    ) {
      return true;
    }
    return false;
  }, [data]);

  if (loading)
    return <Busy busy={loading} busyText={language.Loading_Checkout} />;

  if (error || !data) return <ErrorPage language={language} config={config} />;

  if (
    data.subscriptionById.isCancelled ||
    data.subscriptionById.isAborted ||
    onboardingHasExpired
  ) {
    return (
      <SubscriptionCancelled
        language={language}
        config={config}
        onboardingHasExpired={onboardingHasExpired}
      />
    );
  }

  if (
    data.subscriptionById.subscriptionAlreadyRegistered &&
    !updateSubscription
  ) {
    navigate(
      `/receipt/${data.subscriptionById.finalizingRegistrationChargeSaleId}`
    );
    return null;
  }

  return (
    <>
      {updateSubscription ? (
        <UpdateSubscription
          data={data}
          language={language}
          updateSubscription={updateSubscription}
        />
      ) : (
        <AppLayout pageName="subscription">
          <Header hideUser={true} config={config} language={language} />
          <ContentLayout>
            <SubscriptionModal
              goToPayment={goToPayment}
              language={language}
              data={data}
              subscriptionId={subscriptionId}
            />
            <SubscriptionInfo data={data} language={language} />
          </ContentLayout>
          <SubscriptionTerms setGoToPayment={setGoToPayment} />
        </AppLayout>
      )}
    </>
  );
};

export default Subscription;
